import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  LayoutDashboard, Users, ArrowDownLeft, ArrowUpRight, Layers, RefreshCw, BookOpen,
  Briefcase, GitBranch, Scale, ClipboardCheck, Coins, Landmark, Truck,
  Wallet, CheckSquare, FileText, Mail, ListTodo, FolderOpen, BarChart3,
  Settings, Shield, Menu, X, Search, Plus, Bell,
} from 'lucide-react';
import { dealMgmt } from '../../services/dealManagementApi';
import { initials } from '../../lib/format';
import { roleLabel } from '../../lib/permissions';

const Icon = ({ name, size = 14 }) => {
  const map = {
    dashboard: LayoutDashboard,
    investors: Users,
    deposits: ArrowDownLeft,
    withdrawals: ArrowUpRight,
    'legacy-pool': Layers,
    returns: RefreshCw,
    'xero-journal': BookOpen,
    'dm-dashboard': Briefcase,
    'dm-pipeline': GitBranch,
    'dm-ic': Scale,
    'dm-tl-qc': ClipboardCheck,
    'dm-capital': Coins,
    'dm-finance': Landmark,
    'dm-projects': Truck,
    'dm-repayment': Wallet,
    'dm-reconciliation': CheckSquare,
    'dm-registers': FileText,
    'dm-templates': Mail,
    'dm-tasks': ListTodo,
    'dm-documents': FolderOpen,
    'dm-emails': Mail,
    'kpi-dashboard': BarChart3,
    reports: BarChart3,
    'admin-registers': Shield,
    settings: Settings,
  };
  const C = map[name] || Briefcase;
  return <C size={size} strokeWidth={1.75} />;
};

const navConfig = [
  {
    group: 'Dashboard',
    items: [
      { id: 'dashboard', label: 'Dashboard' },
    ],
  },
  {
    group: 'Capital Management',
    items: [
      { id: 'investors', label: 'Investors' },
      { id: 'deposits', label: 'Deposits', ids: { capitalFilter: 'deposits' } },
      { id: 'withdrawals', label: 'Withdrawals' },
      { id: 'legacy-pool', label: 'Legacy pool' },
      { id: 'returns', label: 'Returns' },
      { id: 'xero-journal', label: 'Xero journal' },
    ],
  },
  {
    group: 'Operations',
    items: [
      { id: 'dm-dashboard', label: 'Ops dashboard' },
      { id: 'dm-pipeline', label: 'Pipeline' },
      { id: 'dm-ic', label: 'IC queue' },
      { id: 'dm-tl-qc', label: 'Team Lead QC' },
      { id: 'dm-capital', label: 'Capital allocation' },
      { id: 'dm-finance', label: 'Finance queue' },
      { id: 'dm-projects', label: 'Projects / delivery' },
      { id: 'dm-repayment', label: 'Repayment' },
      { id: 'dm-reconciliation', label: 'Reconciliation' },
      { id: 'dm-registers', label: 'Registers' },
      { id: 'dm-templates', label: 'Templates' },
      { id: 'dm-tasks', label: 'Tasks' },
      { id: 'dm-documents', label: 'Documents' },
      { id: 'dm-emails', label: 'Emails' },
    ],
  },
  {
    group: 'Platform',
    items: [
      { id: 'kpi-dashboard', label: 'KPI dashboard' },
      { id: 'reports', label: 'Reports' },
      { id: 'admin-registers', label: 'Admin registers' },
      { id: 'settings', label: 'Settings' },
    ],
  },
];

const OPS_PAGES = new Set([
  'dm-dashboard', 'dm-pipeline', 'dm-ic', 'dm-tl-qc', 'dm-capital', 'dm-finance',
  'dm-projects', 'dm-repayment', 'dm-reconciliation', 'dm-registers', 'dm-templates',
  'dm-tasks', 'dm-documents', 'dm-emails', 'dm-new-deal', 'dm-deal-detail', 'kpi-dashboard',
]);

export const pageMeta = {
  dashboard: { title: 'Dashboard', sub: 'Executive overview' },
  notifications: { title: 'Notifications', sub: 'Alerts & task centre' },
  investors: { title: 'Investors', sub: 'Legacy investor management' },
  deposits: { title: 'Deposits', sub: 'Investor deposits & capital inflows' },
  withdrawals: { title: 'Withdrawals', sub: 'Investor withdrawal requests' },
  'inv-profile': { title: 'Investor profile', sub: 'Legacy investor detail' },
  'legacy-pool': { title: 'Legacy pool', sub: 'Pooled capital overview' },
  returns: { title: 'Returns engine', sub: 'Return cycles & payouts' },
  'xero-journal': { title: 'Xero journal', sub: 'Investor return provision' },
  institutions: { title: 'Institutional capital', sub: 'Facilities & drawdowns' },
  'inst-profile': { title: 'Facility profile', sub: 'Institution detail' },
  allocation: { title: 'Capital allocation', sub: 'Pool deployment & waterfall' },
  deals: { title: 'Capital deals', sub: 'Legacy capital allocation deals' },
  'deal-detail': { title: 'Capital deal detail', sub: 'Allocation & repayment' },
  reports: { title: 'Reports', sub: 'Export-ready reports' },
  audit: { title: 'Audit trail', sub: 'Change log & activity' },
  settings: { title: 'Settings', sub: 'Platform configuration' },
  'dm-dashboard': { title: 'Ops dashboard', sub: 'Deal management overview' },
  'dm-pipeline': { title: 'Deal pipeline', sub: 'Table & kanban views' },
  'dm-new-deal': { title: 'New deal', sub: 'Lead capture wizard' },
  'dm-deal-detail': { title: 'Deal detail', sub: 'Full deal workflow' },
  'dm-ic': { title: 'IC queue', sub: 'Investment committee review' },
  'dm-tl-qc': { title: 'Team Lead QC', sub: 'QC checklist queue' },
  'dm-capital': { title: 'Capital allocation queue', sub: 'Funding lines' },
  'dm-finance': { title: 'Finance queue', sub: 'Handover & payments' },
  'dm-projects': { title: 'Projects', sub: 'Delivery monitoring' },
  'dm-repayment': { title: 'Repayment queue', sub: 'Collections & receipts' },
  'dm-reconciliation': { title: 'Reconciliation', sub: 'Close-out queue' },
  'dm-registers': { title: 'Registers', sub: 'End-clients & directors' },
  'dm-templates': { title: 'Templates', sub: 'Email & contract templates' },
  'dm-tasks': { title: 'Tasks', sub: 'Team task board' },
  'dm-documents': { title: 'Documents', sub: 'Deal document search' },
  'dm-emails': { title: 'Emails', sub: 'Deal email threads' },
  'kpi-dashboard': { title: 'KPI dashboard', sub: 'Operating metrics' },
  'admin-registers': { title: 'Admin registers', sub: 'Platform registers' },
};

function NavBody({ activePage, onNavigate, onSignOut, user, onClose }) {
  const handleNav = (item) => {
    onNavigate(item.navTo || item.id, item.ids || {});
    onClose?.();
  };

  return (
    <>
      <div className="sidebar-logo">
        <div className="logo-mark">
          <img src="/logo.webp" alt="huhslCap" />
        </div>
      </div>

      {navConfig.map(group => (
        <div className="nav-group" key={group.group}>
          <div className="nav-label">{group.group}</div>
          {group.items.map(item => {
            const isActive = activePage === item.id;
            return (
              <div
                key={item.id}
                className={`nav-item ${isActive ? 'active' : ''}`}
                onClick={() => handleNav(item)}
              >
                <span className="nav-icon"><Icon name={item.id} /></span>
                {item.label}
              </div>
            );
          })}
        </div>
      ))}

      <div className="sidebar-bottom">
        <div className="user-row">
          <div className="avatar" style={{
            background: 'var(--accent)',
            width: 30, height: 30,
            fontSize: 10.5, color: '#fff',
          }}>
            {initials(user?.fullName || user?.full_name || '')}
          </div>
          <div>
            <div className="u-name">{user?.fullName || user?.full_name || 'User'}</div>
            <div className="u-role">{roleLabel(user)}</div>
          </div>
          <button className="signout-btn" onClick={onSignOut} title="Sign out">→</button>
        </div>
      </div>
    </>
  );
}

/* ─── SIDEBAR ──────────────────────────────────────────────────────────────── */
export const Sidebar = ({ activePage, onNavigate, onSignOut, user, mobileOpen, onMobileClose }) => (
  <>
    <div className="sidebar desktop-sidebar">
      <NavBody activePage={activePage} onNavigate={onNavigate} onSignOut={onSignOut} user={user} />
    </div>
    <div className={`mobile-nav-overlay ${mobileOpen ? 'open' : ''}`} onClick={onMobileClose} />
    <div className={`sidebar mobile-sidebar ${mobileOpen ? 'open' : ''}`}>
      <button className="mobile-nav-close" onClick={onMobileClose} aria-label="Close menu">
        <X size={18} />
      </button>
      <NavBody
        activePage={activePage}
        onNavigate={onNavigate}
        onSignOut={onSignOut}
        user={user}
        onClose={onMobileClose}
      />
    </div>
  </>
);

/* ─── TOPBAR ─────────────────────────────────────────────────────────────────*/
export const Topbar = ({ activePage, onNavigate, user, onMenuToggle }) => {
  const meta = pageMeta[activePage] || { title: activePage, sub: '' };
  const showNewDeal = OPS_PAGES.has(activePage);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const wrapRef = useRef(null);

  const runSearch = useCallback(async (term) => {
    if (!term || term.trim().length < 2) {
      setResults(null);
      return;
    }
    setSearching(true);
    try {
      const res = await dealMgmt.search(term.trim());
      setResults(res);
    } catch {
      setResults({ deals: [], error: true });
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => runSearch(q), 280);
    return () => clearTimeout(t);
  }, [q, runSearch]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setResults(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const deals = results?.deals || results?.results || [];
  const showDrop = results != null && q.trim().length >= 2;

  return (
    <div className="topbar">
      <button className="icon-btn mobile-menu-btn" onClick={onMenuToggle} aria-label="Open menu">
        <Menu size={16} />
      </button>
      <div className="tb-title">{meta.title}</div>
      <div className="tb-sep" />
      <div className="tb-sub">{meta.sub}</div>
      <div className="tb-spacer" />

      {showNewDeal && (
        <button className="btn btn-p btn-sm" onClick={() => onNavigate('dm-new-deal')}>
          <Plus size={14} style={{ marginRight: 4 }} /> New deal
        </button>
      )}

      <div className="search-wrap search-wrap-live" ref={wrapRef}>
        <Search size={13} style={{ color: 'var(--text3)', flexShrink: 0 }} />
        <input
          type="text"
          placeholder="Search deals…"
          value={q}
          onChange={e => setQ(e.target.value)}
          onFocus={() => q.trim().length >= 2 && runSearch(q)}
        />
        {showDrop && (
          <div className="search-dropdown">
            {searching && <div className="search-drop-item muted">Searching…</div>}
            {!searching && deals.length === 0 && (
              <div className="search-drop-item muted">No matches</div>
            )}
            {!searching && deals.slice(0, 8).map(d => (
              <div
                key={d.id}
                className="search-drop-item"
                onClick={() => {
                  onNavigate('dm-deal-detail', { dmDealId: d.id });
                  setQ('');
                  setResults(null);
                }}
              >
                <div style={{ fontWeight: 600 }}>{d.deal_ref || d.ref}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{d.client_company_name || d.name}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="icon-btn" onClick={() => onNavigate('notifications')} title="Notifications">
        <Bell size={14} /><span className="notif-dot" />
      </div>
      <div className="icon-btn" onClick={() => onNavigate('settings')} title="Settings">
        <Settings size={14} />
      </div>

      <div
        className="avatar"
        title={user?.fullName || ''}
        style={{ background: 'var(--accent)', fontSize: 10, color: 'var(--brand-dark)', cursor: 'pointer', width: 28, height: 28 }}
      >
        {initials(user?.fullName || user?.full_name || '')}
      </div>
    </div>
  );
};
